import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import InfiniteScroll from '../InfiniteScroll/InfiniteScroll';
import Notice from '../Notice/Notice';
import Progressbar from '../Progressbar/Progressbar';
import SelectionHandler from '../../lib/SelectionHandler';
import createFakeEvent from '../../lib/createFakeEvent';

const defaultRenderer = item => <div className="default-renderer">{item.title || item}</div>;

/**
 * `List` is a general purpose scrollable list component that supports pagination of the content and customizable cell
 * rendering. You may pass arrays that contain any type of data as long as your *itemRenderer* knows how to visualize each
 * item.
 *
 * The list will be single column by default, but you can make the list into a grid by setting *multiColumn* to `true`.
 *
 * Items in the list can be selected and `List` supports both single and multiple selections (and in multiple ranges)
 * depending on the value of *selectionType*. Keyboard navigation is also supported, but only when the *isFocused* prop is
 * set to `true`.
 *
 * Pagination is handled by 3 functions, *hasMore*, *loadMore* and *pageStart*.
 *
 * @property {any} children Child components will be placed before the first item in *items*. The children will scroll with
 * the rest of the items by default.
 * @property {string} className The class name for this list. Will be appended to the following classes "core-list". In
 * addition the class "focus" will be set whenever the component has the input focus, "error" if *error* is set and *items*
 * is empty, "empty" if *items* is empty, and "disabled" if *disabled* is set to `true`.
 * @property {boolean} disabled If set to `true` the list will be disabled and you will not be able to select items or open
 * the item's action menu. Defaults to `false`.
 * @property {string} emptyText The text that will be shown when items is empty. Defaults to "No items found".
 * @property {string} error Error message to indicate e.g. error loading data. The error will not be displayed if there are
 * data in *items*.
 * @property {function} hasMore A callback that should return `true` when there are more content to be loaded. *loadMore*
 * will only be called as long as *hasMore* returns `true`.
 * *loadMore* when *pageStart* returns a different page index that is not equal to the last one returned.
 * @property {boolean} isFocused If set to `true`, the `List` will have the input focus and the user will be able to use the
 * arrow keys to navigate up and down in the list. "focus" will be appended to the class name. Defaults to `false`.
 * @property {object} itemStyle Callback for returning custom inline styling for each item in the list. Should only be used
 * in rare cases. **Image Bank** uses this prop to get masonry layout working.
 * @property {Array} items The items in this list. The *itemRenderer* callback will be called for each item to render items.
 * @property {boolean} isSelectionAllowed A callback for determining whether an item is selectable or not. The item will
 * not be selected if callback returns `false` and any current selection will be unchanged.
 * @property {function} itemRenderer The cell renderer callback that will be called for all items in *items*. `List` will
 * render the `item.title` if item is an object. If not, the item will be displayed as a string.
 * @property {boolean} loading A busy indicator will be shown when this prop is set to true. Defaults to `false`.
 * @property {function} loadMore A callback that triggers loading of more content. *loadMore* will only be called as long as
 * *hasMore* returns `true`.
 * @property {boolean} multiColumn By default the up and down arrow keys when the list has the input focus will select the
 * previous and next item respectively. Settings this prop to `true` will make `List` calculate the nearest neighbour item in
 * the list when using the arrow keys to navigate. Does not change the layout of the component - you have to use CSS to do
 * that. Defaults to `false`.
 * @property {function} onBackgroundClick A callback that is called whenever the user has clicked on the background of the
 * list (list container).
 * @property {function} onClick Callback for whenever an item is clicked. Both *onSelectionChanged* and *onClick* will be
 * called if both callbacks are registered. *onClick*, however, will only be passed the single, latest item clicked, even if
 * the user selects a range.
 * @property {function} onDoubleClick Callback for whenever a list item is double clicked.
 * @property {function} onKeyDown A callback that is called whenever the user press a key. Will only be called when the
 * *isFocused* prop is set to `true`.
 * @property {function} onSelectionChanged Callback for whenever the selection changes. *isSelectionAllowed* will be
 * called first and if it returns `false` *onSelectionChanged* will never be invoked.
 * @property {function} pageStart A callback which returns the next page index. `List` will only call *hasMore* and
 * *loadMore* when *pageStart* returns a different page index that is not equal to the last one returned.
 * @property {number} selectionType The selection type. Must be one of `List.NONE`, `List.SINGLE` or `List.MULTIPLE`.
 * Defaults to `List.SINGLE`.
 *
 * @see InfiniteScroll, Progressbar, Renderers
 */
class List extends Component {

  static displayName = 'List';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    emptyText: PropTypes.string,
    error: PropTypes.string,
    hasMore: PropTypes.func,
    isFocused: PropTypes.bool,
    itemStyle: PropTypes.func,
    items: PropTypes.array,
    isSelectionAllowed: PropTypes.func,
    itemRenderer: PropTypes.func,
    loading: PropTypes.bool,
    loadMore: PropTypes.func,
    multiColumn: PropTypes.bool,
    onBackgroundClick: PropTypes.func,
    onClick: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    onSelectionChanged: PropTypes.func,
    pageStart: PropTypes.func,
    selectionType: PropTypes.number
  };

  static defaultProps = {
    emptyText: 'No items found',
    isSelectionAllowed: () => true,
    itemRenderer: defaultRenderer,
    multiColumn: false,
    selectionType: SelectionHandler.SINGLE
  };

  constructor(props) {
    super(props);
    this.selectionHandler = new SelectionHandler(props.selectionType);
    if (props.isFocused) {
      window.addEventListener('keydown', this.onKeyDown);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectionType !== prevProps.selectionType) {
      this.selectionHandler = new SelectionHandler(this.props.selectionType);
    }
    if (this.props.isFocused && !prevProps.isFocused) {
      window.addEventListener('keydown', this.onKeyDown);
    } else if (!this.props.isFocused && prevProps.isFocused) {
      window.removeEventListener('keydown', this.onKeyDown);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  onBackgroundClick = e => {
    const node = this.refNode && this.getDOMNode();

    if ((node && node.childNodes && node.childNodes.length && e.target === node.childNodes[0]) || e.target === node) {
      e.preventDefault();
      e.stopPropagation();
      if (this.props.onBackgroundClick) {
        this.props.onBackgroundClick(e);
      }
    }
  };

  onDoubleClick = e => {
    const { disabled, onDoubleClick, isSelectionAllowed } = this.props;

    if (!disabled) {
      let index;

      if (typeof e === 'number') {
        index = e;
      } else {
        e.preventDefault();
        e.stopPropagation();
        index = Array.prototype.indexOf.call(this.refList.childNodes, e.currentTarget);
      }
      const item = this.props.items[index];

      if (onDoubleClick && isSelectionAllowed(item, e)) {
        onDoubleClick(item, e);
      }
    }
  };

  onKeyDown = e => {
    if (this.refs && this.refList) {
      const { isFocused, multiColumn, onKeyDown, onSelectionChanged, selectionType } = this.props;
      const node = this.refNode && this.refNode.getDOMNode();
      const MULTIPLE = SelectionHandler.MULTIPLE;

      if (isFocused && (e.target === node || e.target === this.refList || (e.target && e.target.localName === 'body'))) {
        if (e.keyCode >= 37 && e.keyCode <= 40) { // arrow keys
          e.preventDefault();
          e.stopPropagation();

          const { items } = this.props;
          const selected = items.filter(item => item.selected);
          let index = items.indexOf(selected[selected.length - 1]);

          index = this.getSelectionStart(index);
          if ((e.keyCode === 37 || e.keyCode === 39) && !multiColumn && onKeyDown) {
            onKeyDown(e, this.getSelectionOffset());
            return; // premature return
          } else if (multiColumn) {
            if (e.keyCode === 39) { // right
              index = Math.min(index + 1, items.length - 1);
            } else if (e.keyCode === 37) { // left
              index = Math.max(0, index - 1);
            } else if (e.keyCode === 38) { // up
              index = this.findClosestItem(index, -1);
            } else if (e.keyCode === 40) {
              index = this.findClosestItem(index, +1);
            }
          } else if (e.keyCode === 38) { // up
            index = Math.max(0, index - 1);
          } else if (e.keyCode === 40) { // down
            index = Math.min(items.length - 1, index + 1);
          }
          this.scrollToElement(index);
          this.setSelectedIndex(index, e);
        } else if (e.keyCode === 65 && (e.metaKey || e.ctrlKey) && onSelectionChanged && selectionType === MULTIPLE) {
          e.preventDefault();
          e.stopPropagation();

          const all = SelectionHandler.selectAll(this.props.items);
          onSelectionChanged(all, e);
        } else if (e.keyCode === 13) {
          const { items } = this.props;
          const selected = items.filter(item => item.selected);
          const index = items.indexOf(selected[selected.length - 1]);

          this.onDoubleClick(index);
        } else if (e.code === 'PageDown') {
          let start = this.getSelectionStart();
          const height = node.clientHeight * 0.9; // HACK browsers scroll less than the visible height

          if (start >= 0 && node.scrollHeight > height) {
            const scrollTop = node.scrollTop + height;
            const children = [].slice.call(this.refList.children);
            let index = children.findIndex(item => item.offsetTop >= scrollTop);

            if (index === -1) {
              index = children.length - 1;
            }
            this.setSelectedIndex(index, e);
          }
        } else if (e.code === 'PageUp') {
          const selection = this.selectionHandler.getSelection();
          const end = Math.max(selection.start, selection.end);
          const height = node.clientHeight * 0.9; // HACK browsers scroll less than the visible height

          if (end >= 0) {
            const scrollTop = Math.max(0, node.scrollTop - height);
            const children = [].slice.call(this.refList.children);
            const index = children.findIndex(item => item.offsetTop >= scrollTop);

            this.setSelectedIndex(Math.max(0, index), e);
          }
        }
        if (onKeyDown) {
          onKeyDown(e);
        }
      }
    }
  };

  onSelectionChanged = e => {
    const { disabled, items, onSelectionChanged, isSelectionAllowed, onClick } = this.props;

    if (!disabled && !e.defaultPrevented) {
      const index = Array.prototype.indexOf.call(this.refList.childNodes, e.currentTarget);

      if (isSelectionAllowed(items[index], e)) {
        const newItems = this.selectionHandler.getSelectedItems(items, index, e);

        if (onSelectionChanged) {
          e.stopPropagation();
          onSelectionChanged(newItems, e);
        }
        if (onClick) {
          onClick(items[index], e);
        }
      }
    }
  };

  getDOMNode = () => this.refNode && this.refNode.getDOMNode();

  getSelectionOffset = () => {
    const { items } = this.props;
    let y = 0;

    if (items && items.length) {
      const selection = this.selectionHandler.getSelection();
      const list = this.refList;

      if (selection.start >= 0 && selection.start < list.childNodes.length) {
        const item = list.childNodes[selection.start];
        const scroller = list.parentNode;
        const rect = item.getClientRects()[0];

        y = Math.max(0, Math.min(scroller.clientHeight, item.offsetTop + rect.height / 2 - scroller.scrollTop));
      }
    }
    return y;
  };

  getSelectionStart = index => {
    const selection = this.selectionHandler.getSelection();

    if (selection.end <= selection.start) {
      return selection.end;
    }
    return index;
  };

  setSelectedIndex = (index, event) => this.onSelectionChanged({
    ...createFakeEvent(event && event.target && event.target.value, event),
    currentTarget: this.refList.childNodes[index],
  });

  setSelectionAtOffset = y => {
    if (this.refList) {
      const list = this.refList;
      const children = list.childNodes;
      const len = children.length;
      const yy = y + list.parentNode.scrollTop;
      let i = 0;
      let top = 0;

      for (; i < len - 1; i++) {
        top += children[i].getClientRects()[0].height;
        if (yy <= top) {
          break;
        }
      }
      this.setSelectedIndex(i);
      this.scrollToElement(i);
    }
  };

  findClosestItem = (index, direction) => {
    if (index >= 0) {
      const children = [].slice.call(this.refList.children);
      const y = children[index].offsetTop;
      const x = children[index].offsetLeft + children[index].clientWidth / 2;
      let row;

      if (direction === 1) {
        row = children.filter(el => {
          if (el.offsetTop > y && !row) {
            row = el.offsetTop;
          }
          return el.offsetTop === row;
        });
      } else {
        row = children.slice(0)
          .reverse()
          .filter(el => {
            if (el.offsetTop < y && !row) {
              row = el.offsetTop;
            }
            return el.offsetTop === row;
          })
          .reverse();
      }
      const item = row.filter(el => x >= el.offsetLeft && x <= el.offsetLeft + el.offsetWidth + el.clientLeft * 2);
      if (item.length > 0) {
        return children.indexOf(item[0]);
      }
      if (row.length > 0) {
        return children.indexOf(row[row.length - 1]);
      }
    }
    return index;
  };

  scrollToElement = index => {
    const el = this.refList.children.length > index ? this.refList.children[index] : undefined;

    if (el) {
      if (el.scrollIntoViewIfNeeded) {
        el.scrollIntoViewIfNeeded(true);
      }
    }
  };

  render() {
    const { disabled, itemRenderer, itemStyle, error, emptyText, hasMore, loading, loadMore, pageStart } = this.props;
    let { items = [] } = this.props;
    const cls = cx(
      'core-list core-infinite-scroll-wrapper',
      this.props.className,
      {
        disabled,
        error: error && items.length === 0,
        empty: !error && items.length === 0,
        focus: this.props.isFocused
      }
    );

    if (items.length) {
      items = items.map((item, index) =>
        <li key={item.id || index} className={cx('list-item', { selected: item.selected })}
          style={itemStyle ? itemStyle(item, index) : undefined}
          onClick={this.onSelectionChanged} onDoubleClick={this.onDoubleClick}>
          {itemRenderer(item, index, this.props)}
        </li>
      );
    } else if (error) {
      items = <li className="error"><Notice errorMessage={error}/></li>;
    } else if (emptyText && emptyText.length > 0) {
      items = <li className="no-items">{emptyText}</li>;
    }
    return (
      <InfiniteScroll className={cls} onClick={this.onBackgroundClick} ref={c => { this.refNode = c; }} hasMore={hasMore} loadMore={loadMore} pageStart={pageStart}>
        {this.props.children}
        <ul ref={c => { this.refList = c; }} className={cx('core-list-items', {loading})}>
          {items}
        </ul>
        {loading && <Progressbar className="core-infinite-scroll-progress" circular indeterminate/>}
      </InfiniteScroll>
    );
  }

}

List.NONE = SelectionHandler.NONE;
List.SINGLE = SelectionHandler.SINGLE;
List.MULTIPLE = SelectionHandler.MULTIPLE;

export default List;
